import {
  X,
  DollarSign,
  Receipt,
  User,
  Calendar,
  CreditCard,
  Hash,
  FileText,
  RotateCcw,
  BadgeCheck,
  Printer,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { joinFullName } from '../../utils/patientName';
import type { Payment } from '../../types/billing';

interface PaymentDetailsModalProps {
  open: boolean;
  onClose: () => void;
  payment: Payment | null;
  onPrint?: (payment: Payment) => void;
}

const CURRENCY = '৳';

const METHOD_LABELS: Record<string, string> = {
  cash: 'Cash',
  card: 'Card',
  upi: 'UPI',
  bank_transfer: 'Bank Transfer',
  insurance: 'Insurance',
};

function fmtMoney(value: number | undefined | null): string {
  return `${CURRENCY}${(Number(value) || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function fmtDateTime(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function patientName(payment: Payment): string {
  const patient = payment.patient;
  const name = patient ? joinFullName(patient) : '';
  return name || payment.patientId?.slice(0, 8) || '—';
}

function receivedByName(payment: Payment): string {
  const user = payment.receivedBy;
  if (!user) return '—';
  return user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || '—';
}

function DetailItem({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-xs font-medium text-text-muted uppercase mb-1 flex items-center gap-1.5">
        <Icon size={12} />
        {label}
      </p>
      <p className="text-sm text-text break-words">{value}</p>
    </div>
  );
}

export function PaymentDetailsModal({ open, onClose, payment, onPrint }: PaymentDetailsModalProps) {
  if (!open || !payment) return null;

  const isRefund = Number(payment.amount) < 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-border bg-background/50 rounded-t-2xl">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                isRefund ? 'bg-danger/10' : 'bg-success/10'
              }`}
            >
              {isRefund ? (
                <RotateCcw size={20} className="text-danger" />
              ) : (
                <DollarSign size={20} className="text-success" />
              )}
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-text truncate">{payment.paymentNumber}</h2>
              <p className="text-xs text-text-muted">{patientName(payment)}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-background rounded-xl transition-colors"
          >
            <X size={18} className="text-text-muted" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="flex items-center justify-between gap-4 rounded-xl border border-border bg-background/50 p-4">
            <div>
              <p className="text-xs font-medium text-text-muted uppercase">
                {isRefund ? 'Refunded Amount' : 'Amount Received'}
              </p>
              <p className={`text-2xl font-bold mt-1 ${isRefund ? 'text-danger' : 'text-success'}`}>
                {fmtMoney(payment.amount)}
              </p>
            </div>
            <span
              className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                isRefund ? 'bg-danger/10 text-danger' : 'bg-primary/10 text-primary'
              }`}
            >
              {isRefund ? <RotateCcw size={14} /> : <BadgeCheck size={14} />}
              {isRefund ? 'Refund' : METHOD_LABELS[payment.method] || payment.method}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <DetailItem icon={Receipt} label="Invoice" value={payment.invoice?.invoiceNumber || '—'} />
            <DetailItem icon={User} label="Patient" value={patientName(payment)} />
            <DetailItem
              icon={CreditCard}
              label="Method"
              value={METHOD_LABELS[payment.method] || payment.method || '—'}
            />
            <DetailItem icon={Hash} label="Reference" value={payment.transactionId || '—'} />
            <DetailItem icon={Calendar} label="Payment Date" value={fmtDateTime(payment.paymentDate)} />
            <DetailItem icon={User} label="Received By" value={receivedByName(payment)} />
          </div>

          {payment.invoice && (
            <div className="rounded-lg border border-border bg-background/50 p-3 text-sm space-y-1">
              <p className="text-xs font-medium text-text-muted uppercase mb-1">Invoice Position</p>
              <div className="flex justify-between">
                <span className="text-text-muted">Total</span>
                <span className="text-text">{fmtMoney(payment.invoice.total)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">Paid</span>
                <span className="text-success">{fmtMoney(payment.invoice.paidAmount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-text-muted">Due</span>
                <span className="text-danger">{fmtMoney(payment.invoice.dueAmount)}</span>
              </div>
            </div>
          )}

          <div className="pt-4 border-t border-border">
            <p className="text-xs font-medium text-text-muted uppercase mb-1 flex items-center gap-1.5">
              <FileText size={12} />
              Notes
            </p>
            <p className="text-sm text-text whitespace-pre-wrap">{payment.notes || 'No notes provided.'}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-border">
            <DetailItem icon={Calendar} label="Created" value={fmtDateTime(payment.createdAt)} />
            <DetailItem icon={Calendar} label="Last updated" value={fmtDateTime(payment.updatedAt)} />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-border">
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
          {onPrint && (
            <Button type="button" onClick={() => onPrint(payment)}>
              <Printer size={16} className="mr-2" />
              Print Receipt
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
